import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import CustomTextInput from "../../../components/common/CustomTextInput";
import CustomSelect from "../../../components/common/CustomSelect";
import CustomButton from "../../../components/common/CustomButton/CustomButton";
import axios from "axios";
import { addNewLead } from "../../../utils/urls";
import { errorToast, successToast } from "../../../utils/toasts";
import { useNavigate } from "react-router-dom";
import {
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";

const validationSchema = Yup.object({
  priority: Yup.string().required("Priority is required"),
  additional_notes: Yup.string().optional(),
});

function Review({
  onBack,
  onClose,
  data = {},
  category = null,
  subCategory = null,
  product = null,
  selectedState = null,
  selectedCity = null,
}) {
  const navigate = useNavigate();

  async function addLeadToServer(values, setSubmitting) {
    console.log("values", values);
    setSubmitting(true);

    const postBody = {
      name: data?.name,
      email: data?.email,
      contact_number: data?.contact_number,
      dob: data?.dob,
      address: data?.address,
      city_id: data?.city_id,
      priority: values?.priority,
      additinal_note: values?.additional_notes,
    };

    if (product) {
      postBody.product_id = product?.product_id;
    } else if (category) {
      postBody.category_id = category?.category_id;
      if (subCategory) {
        postBody.sub_category_id = subCategory?.sub_category_id;
      }
    } else {
      errorToast("Please select category or product");
      setSubmitting(false);
    }

    axios
      .post(addNewLead, postBody)
      .then((response) => {
        console.log("response", response);
        if (response?.data?.success) {
          successToast("Lead added successfully");
          // navigate(`/leads/details/${response?.data?.data?.lpr_id}`);
          onClose();
        } else {
          throw response?.data;
        }
      })
      .catch((error) => {
        console.log("error", error);
        errorToast(
          error?.response?.data?.error?.message ||
            "Something went wrong, try again"
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  }

  return (
    <Formik
      initialValues={{
        priority: "High",
        additional_notes: "",
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        addLeadToServer(values, setSubmitting);
      }}
    >
      {({ isSubmitting }) => (
        <Form className="pt-4 border-t border-[#E2EBF3]">
          <p className="text-[1rem] font-semibold text-[#2C2C2C] mb-4">
            Lead Review
          </p>

          <div className="flex flex-col flex-1 h-[calc(100vh_-_24rem)] overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Lead Info */}
              <div className="border p-4 rounded-lg space-y-3">
                <div className="text-lg font-semibold text-gray-800">
                  {data?.name || "Lead Name"}
                </div>
                {/* <div className="text-sm text-gray-500">#{data?.id || "#ID"}</div> */}

                <div className="text-sm">
                  <div className="flex flex-col gap-2">
                    {[
                      {
                        label: "Email",
                        icon: (
                          <EnvelopeIcon className="size-5 text-[#2F4E74]" />
                        ),
                        value: data.email,
                      },
                      {
                        label: "Mobile No.",
                        icon: <PhoneIcon className="size-5 text-[#2F4E74]" />,
                        value: data.contact_number,
                      },
                      {
                        label: "Date of Birth",
                        icon: (
                          <CalendarIcon className="size-5 text-[#2F4E74]" />
                        ),
                        value: data.dob,
                      },
                      {
                        label: "Address",
                        icon: <MapPinIcon className="size-5 text-[#2F4E74]" />,
                        value: data.address,
                      },
                      {
                        label: "City",
                        icon: <MapPinIcon className="size-5 text-[#2F4E74]" />,
                        value: selectedCity?.title,
                      },
                      {
                        label: "State",
                        icon: <MapPinIcon className="size-5 text-[#2F4E74]" />,
                        value: selectedState?.title,
                      },
                    ].map((item, index) => (
                      <div className="flex flex-row gap-4" key={index}>
                        <div className="flex flex-row gap-2 min-w-[120px] max-sm:min-w-[100px]">
                          {item.icon}
                          <p className="text-[0.875rem] font-[500] text-[#2F4E74]">
                            {item.label}
                          </p>
                        </div>
                        <div>
                          <p className="text-[0.875rem] font-[500] text-[#213650]">
                            {item.value}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Plan Info */}
              {product ? (
                <ProductCard data={product} />
              ) : subCategory ? (
                <SubCategoryCard data={subCategory} />
              ) : (
                <SubCategoryCard data={category} />
              )}
            </div>
            {/* Priority and Notes */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <CustomSelect
                required
                name="priority"
                label="Select priority"
                placeholder="Select priority"
                menuList={[
                  { label: "High", value: "High" },
                  { label: "Medium", value: "Medium" },
                  { label: "Low", value: "Low" },
                ]}
              />

              <CustomTextInput
                name="additional_notes"
                label="Additional notes"
                placeholder="Write notes here"
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-between items-center mt-6">
            <CustomButton
              type="button"
              variant="outlined"
              onClick={onBack}
              label="Back"
              disabled={isSubmitting}
            />
            <div className="flex gap-2">
              <CustomButton
                type="submit"
                // className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                variant="contained"
                label="Submit"
                isLoading={isSubmitting}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default Review;

const ProductCard = ({ data }) => {
  return (
    <div
      className={`border rounded-lg p-4 shadow-sm bg-white cursor-pointer transition-all duration-200`}
    >
      <div className="flex gap-3 items-start">
        <img
          src={data?.company_logo}
          alt="company-logo"
          className="w-12 h-12 object-contain rounded-md"
        />
        <div className="flex flex-col gap-1">
          <p className="font-semibold text-gray-800 leading-snug">
            {data?.company_name}
          </p>
          <p className="text-sm font-medium text-gray-600 leading-tight">
            {data?.product_name}
          </p>
        </div>
      </div>

      <div className="mt-3">
        <p className="text-sm font-semibold text-gray-700 mb-1">Description:</p>
        <p className="text-sm text-gray-600 line-clamp-3">
          {data?.description}
        </p>
      </div>

      <div className="mt-2">
        <p className="text-sm font-semibold text-gray-700 mb-1">
          Financial Description:
        </p>
        <p className="text-sm text-gray-600">{data?.financial_description}</p>
      </div>

      {/* <div className="mt-3 text-sm font-medium text-blue-600 hover:underline">
        View Details
      </div> */}
    </div>
  );
};

const SubCategoryCard = ({ data }) => {
  return (
    <div
      className={`bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 p-6 border flex flex-col`}
    >
      <div className="flex items-center gap-4 mb-4">
        <img
          src={data.logo_url}
          alt={data.title}
          className="w-14 h-14 object-contain rounded-full border border-gray-200"
        />
        <h2 className="text-lg font-semibold text-gray-800">{data.title}</h2>
      </div>

      <p
        className="text-sm text-gray-600 overflow-hidden text-ellipsis"
        style={{ maxHeight: "4.5em" }}
      >
        {data.description}
      </p>
    </div>
  );
};
